import MenuIcon from '@mui/icons-material/Menu';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import NotificationsIcon from '@mui/icons-material/Notifications';
import {
  AppBar,
  Badge,
  Box,
  Button,
  IconButton,
  Link,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import React, { useState, useCallback } from 'react';

import { brand } from '@penticton-dev-events/shared';
import { useAuth } from '../app/providers/AuthProvider';
import { useColorMode, useAppTheme } from '../app/providers/ThemeProvider';
import { brandAssets, getBrandGradient } from '../theme/tokens';
import NavDrawer from './navigation/NavDrawer';
import AuthDialog from '../features/auth/components/AuthDialog';
import UserAvatar from './UserAvatar';
import { useNotifications } from '../features/notifications/hooks/useNotifications';
import { firestore } from '../lib/firebase/client';

/**
 * Brand logo component with SVG support and fallback
 */
const BrandLogo = ({
  variant = 'logo',
  size = 32,
}: {
  variant?: 'logo' | 'wordmark';
  size?: number;
}) => {
  const theme = useAppTheme();
  const src = variant === 'logo' ? '/logo_events.png' : brand.assets.logo.wordmark;

  return (
    <Box
      component="img"
      src={src}
      alt={brand.organizer.name}
      onError={e => {
        // Fallback to brand logo from main site if local assets fail
        const target = e.target as HTMLImageElement;
        if (target.src !== brand.assets.logo.fallback) {
          target.src = brand.assets.logo.fallback;
        }
      }}
      sx={{
        height: size,
        width: 'auto',
        color: 'currentColor',
        filter: theme.palette.mode === 'dark' ? 'brightness(1.1)' : 'none',
      }}
    />
  );
};

const TopNav = React.memo(() => {
  const { mode, toggleColorMode } = useColorMode();
  const { user, profile, signOut, loading } = useAuth();
  const theme = useAppTheme();
  const { customTokens: tokens } = theme;
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.down('lg'));
  const navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);

  // Get unread notification count
  const { unreadCount } = useNotifications(firestore, user?.uid);

  const handleDrawerToggle = useCallback(() => {
    setDrawerOpen(!drawerOpen);
  }, [drawerOpen]);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const handleOpenAuthDialog = useCallback(() => {
    setAuthDialogOpen(true);
  }, []);

  const handleCloseAuthDialog = useCallback(() => {
    setAuthDialogOpen(false);
  }, []);

  return (
    <AppBar
      component="nav"
      position="sticky"
      color="default"
      elevation={0}
      sx={{
        mb: tokens.spacing[4],
        borderBottom: `1px solid ${theme.palette.divider}`,
        backdropFilter: 'blur(8px)',
        backgroundColor:
          mode === 'light' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(18, 18, 18, 0.95)',
        // Subtle brand gradient overlay
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: getBrandGradient('subtle', mode),
          opacity: 0.03,
          pointerEvents: 'none',
        },
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          edge="start"
          sx={{
            mr: 2,
            display: { xs: 'inline-flex', md: 'none' }, // Only show on mobile/tablet
          }}
          onClick={handleDrawerToggle}
          aria-label="Open navigation menu"
        >
          <MenuIcon />
        </IconButton>
        <Box
          component={RouterLink}
          to="/"
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            color: 'inherit',
            gap: tokens.spacing[2],
            '&:hover': {
              opacity: 0.8,
              transition: `opacity ${tokens.motion.duration.normal} ${tokens.motion.easing.ease}`,
            },
          }}
        >
          <BrandLogo variant="logo" size={isMobile ? 28 : 32} />
          {!isMobile && (
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: tokens.typography.fontWeight.bold,
                  fontSize: tokens.typography.fontSize.lg,
                  lineHeight: 1.2,
                  background: getBrandGradient('primary', mode),
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textDecoration: 'none',
                }}
              >
                {brand.organizer.name}
                <Box
                  component="sup"
                  sx={{
                    fontSize: '0.5em',
                    ml: 0.5,
                    color: 'text.secondary',
                    fontWeight: tokens.typography.fontWeight.medium,
                  }}
                >
                  beta
                </Box>
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  fontSize: '0.65rem',
                  opacity: 0.7,
                  mt: '-2px',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
              >
                Events
              </Typography>
            </Box>
          )}
        </Box>

        {/* Flexible spacer for centered logo on mobile */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Desktop Navigation Links - Show only on large screens */}
        {!isTablet && (
          <Box>
            <Link
              component={RouterLink}
              color="inherit"
              to="/groups"
              underline="none"
              sx={{
                mr: tokens.spacing[4],
                fontWeight: tokens.typography.fontWeight.medium,
                transition: `color ${tokens.motion.duration.normal} ${tokens.motion.easing.ease}`,
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              Groups
            </Link>
            {profile?.isAdmin && (
              <>
                <Link
                  component={RouterLink}
                  color="inherit"
                  to="/admin"
                  underline="none"
                  sx={{
                    mr: tokens.spacing[4],
                    fontWeight: tokens.typography.fontWeight.medium,
                    transition: `color ${tokens.motion.duration.normal} ${tokens.motion.easing.ease}`,
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  Admin Dashboard
                </Link>
                <Link
                  component={RouterLink}
                  color="inherit"
                  to="/broadcast"
                  underline="none"
                  sx={{
                    mr: tokens.spacing[4],
                    fontWeight: tokens.typography.fontWeight.medium,
                    transition: `color ${tokens.motion.duration.normal} ${tokens.motion.easing.ease}`,
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  Broadcast
                </Link>
              </>
            )}
            {user && (
              <Link
                component={RouterLink}
                color="inherit"
                to="/notifications"
                underline="none"
                sx={{
                  mr: tokens.spacing[4],
                  fontWeight: tokens.typography.fontWeight.medium,
                  transition: `color ${tokens.motion.duration.normal} ${tokens.motion.easing.ease}`,
                  '&:hover': {
                    color: 'primary.main',
                  },
                }}
              >
                Notifications
              </Link>
            )}
            {user && (
              <Link
                component={RouterLink}
                color="inherit"
                to="/profile"
                underline="none"
                sx={{
                  mr: tokens.spacing[4],
                  fontWeight: tokens.typography.fontWeight.medium,
                  transition: `color ${tokens.motion.duration.normal} ${tokens.motion.easing.ease}`,
                  '&:hover': {
                    color: 'primary.main',
                  },
                }}
              >
                Profile
              </Link>
            )}
            <Link
              component={RouterLink}
              color="inherit"
              to="/about"
              underline="none"
              sx={{
                fontWeight: tokens.typography.fontWeight.medium,
                transition: `color ${tokens.motion.duration.normal} ${tokens.motion.easing.ease}`,
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              About
            </Link>
          </Box>
        )}

        <IconButton
          color="inherit"
          onClick={toggleColorMode}
          sx={{
            ml: tokens.spacing[2],
            color: theme.palette.primary.main,
            transition: `all ${tokens.motion.duration.normal} ${tokens.motion.easing.ease}`,
            '&:hover': {
              transform: 'scale(1.05)',
              backgroundColor: theme.palette.primary.main + '10',
              color: theme.palette.primary.main,
            },
          }}
          aria-label={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
        >
          {mode === 'light' ? <NightsStayIcon /> : <WbSunnyIcon />}
        </IconButton>

        {/* Notification Icon with Badge */}
        {user && (
          <IconButton
            color="inherit"
            onClick={() => navigate('/notifications')}
            sx={{
              ml: tokens.spacing[2],
              color: theme.palette.text.primary,
              transition: `all ${tokens.motion.duration.normal} ${tokens.motion.easing.ease}`,
              '&:hover': {
                transform: 'scale(1.05)',
                backgroundColor: 'action.hover',
              },
            }}
            aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
          >
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
        )}

        {user ? (
          <Stack
            alignItems="center"
            direction="row"
            spacing={tokens.spacing[2]}
            sx={{ ml: tokens.spacing[4] }}
          >
            <UserAvatar
              displayName={profile?.displayName}
              photoURL={user.photoURL}
              email={profile?.email}
              sx={{ width: 32, height: 32 }}
            />
            {!isMobile && (
              <Typography variant="body2" color="inherit">
                {profile?.displayName ?? user.email ?? 'Member'}
              </Typography>
            )}
            <Button
              color="inherit"
              onClick={() => void signOut()}
              size="small"
              sx={{
                fontWeight: tokens.typography.fontWeight.medium,
                borderRadius: tokens.radii.lg,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              Sign out
            </Button>
          </Stack>
        ) : (
          <Button
            color="inherit"
            disabled={loading}
            onClick={handleOpenAuthDialog}
            sx={{
              ml: tokens.spacing[4],
              fontWeight: tokens.typography.fontWeight.medium,
              borderRadius: tokens.radii.lg,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            Sign in
          </Button>
        )}
      </Toolbar>

      {/* Navigation Drawer */}
      <NavDrawer open={drawerOpen} onClose={handleDrawerClose} />

      {/* Auth Dialog */}
      <AuthDialog open={authDialogOpen} onClose={handleCloseAuthDialog} />
    </AppBar>
  );
});

TopNav.displayName = 'TopNav';

export default TopNav;
